import React, { useCallback, useMemo } from "react";

import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Destination } from "../../../../models/destination";
import { JobMode } from "../../../../models/job.mode";
import { Location } from "../../../../models/location";
import { ItemJob, QuotedItem, RoomJob } from "../../../../models/sales";
import { convertMetricVolume } from "../../../../util/converts";
import { DESTINATION } from "../../../../util/enums";
import SkeletonComponent from "../../Skeleton";
import useStyles from "./styles";

interface EstimateInventorySectionProps {
  inventory: RoomJob[];
  destinationList: Destination[];
  destinationAddresses: Location[];
  modeDestinationList?: JobMode[];
  loading?: boolean;
  measurementCode?: string;
}

const EstimateInventorySection: React.FC<EstimateInventorySectionProps> = ({
  inventory: inventoryList,
  destinationAddresses,
  modeDestinationList,
  destinationList,
  loading,
  measurementCode,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const getItemCounts = useCallback((items: QuotedItem[]) => {
    let quantity = 0;
    let volume = 0;
    let weight = 0;

    items.forEach((item) => {
      quantity += Number((item?.itemJob as ItemJob).quantity) || 0;
      volume +=
        Number((item?.itemJob as ItemJob).volume) * Number((item?.itemJob as ItemJob).quantity) ||
        0;
      weight += Number((item?.itemJob as ItemJob).weight || 0);
    });

    return { quantity, volume, weight };
  }, []);

  const addressDestinationItems = useMemo(() => {
    const destinationsId = destinationAddresses.map((address) => address.id);
    const destinationItems: { [x: string]: QuotedItem[] } = {};

    destinationsId.forEach((destinationId) => {
      if (destinationId) {
        const items: QuotedItem[] = [];

        inventoryList?.forEach((inventory) => {
          inventory?.itemsJob?.forEach((itemJob) => {
            if (
              itemJob &&
              itemJob.itemJob &&
              itemJob.itemJob.destinationResidenceId &&
              itemJob.itemJob.destinationResidenceId === destinationId
            ) {
              items.push(itemJob);
            }
          });
          inventory?.materials?.forEach((material) => {
            if (
              material &&
              material?.serviceBooking?.isItem &&
              material.destinationResidenceId &&
              material.destinationResidenceId === destinationId
            ) {
              const itemJob = {
                itemJob: material,
              } as QuotedItem;

              items.push(itemJob);
            }
          });
        });
        if (items && items.length > 0) {
          destinationItems[`id-${destinationId.toString()}`] = items;
        }
      }
    });

    return destinationItems;
  }, [destinationAddresses, inventoryList]);

  const customDestinationItems = useMemo(() => {
    const destinationsId = destinationList
      .filter((destination) => !destination.name?.includes(DESTINATION.NOT_GOING))
      .map((destination) => destination.id);

    const destinationItems: { [x: string]: QuotedItem[] } = {};

    destinationsId.forEach((destinationId) => {
      if (destinationId) {
        const items: QuotedItem[] = [];

        inventoryList?.forEach((inventory) => {
          inventory?.itemsJob?.forEach((itemJob) => {
            if (itemJob && itemJob.itemJob && itemJob.itemJob.destinationId === destinationId) {
              items.push(itemJob);
            }
          });
          inventory?.materials?.forEach((material) => {
            if (
              material &&
              material?.serviceBooking?.isItem &&
              material.destinationId &&
              material.destinationId === destinationId
            ) {
              const itemJob = {
                itemJob: material,
              } as QuotedItem;

              items.push(itemJob);
            }
          });
        });

        if (items && items.length > 0) {
          destinationItems[`id-${destinationId.toString()}`] = items;
        }
      }
    });

    return destinationItems;
  }, [destinationList, inventoryList]);

  const customModeDestinationItems = useMemo(() => {
    const modeDestinationsId = modeDestinationList
      ?.filter((modeDestination) => modeDestination.isDestination)
      .map((modeDestination) => modeDestination.id);

    const modeDestinationItems: { [x: string]: QuotedItem[] } = {};

    modeDestinationsId?.forEach((modeDestinationId) => {
      if (modeDestinationId) {
        const items: QuotedItem[] = [];

        inventoryList?.forEach((inventory) => {
          inventory?.itemsJob?.forEach((itemJob) => {
            if (
              itemJob &&
              itemJob.itemJob &&
              itemJob.itemJob?.modeDestinationId === modeDestinationId
            ) {
              items.push(itemJob);
            }
          });
          inventory?.materials?.forEach((material) => {
            if (
              material &&
              material?.serviceBooking?.isItem &&
              material.destinationId &&
              material?.modeDestinationId === modeDestinationId
            ) {
              const itemJob = {
                itemJob: material,
              } as QuotedItem;

              items.push(itemJob);
            }
          });
        });

        if (items && items.length > 0) {
          modeDestinationItems[`id-${modeDestinationId.toString()}`] = items;
        }
      }
    });

    return modeDestinationItems;
  }, [modeDestinationList, inventoryList]);

  const renderInventoryBox = (
    title: string,
    items: QuotedItem[],
    subTitle = "\u200C",
    color?: string
  ) => {
    const { quantity, volume, weight } = getItemCounts(items);

    return (
      <SkeletonComponent isLoading={!!loading}>
        <Grid
          className={classes.inventoryBox}
          data-cy={`estimate-inventory-section-${title}`}
          item
          container
          direction="column"
          justifyContent="space-between"
        >
          <div className={classes.inventoryTop}>
            {title && (
              <Grid item className={classes.inventoryTitle}>
                <span style={{ color }}>{title}</span>
              </Grid>
            )}
            {subTitle && (
              <Grid item>
                <span className={classes.inventorySubTitle}>{subTitle}</span>
              </Grid>
            )}
          </div>
          <div className={classes.inventoryBottom}>
            <Grid item container direction="column">
              <Grid item container direction="row" justifyContent="space-between">
                <span className={classes.inventoryProperty}>{t("quantity")}</span>
                <span className={classes.inventoryValue}>{quantity}</span>
              </Grid>
              <Grid item container direction="row" justifyContent="space-between">
                <span className={classes.inventoryProperty}>{t("volume")}</span>
                <span className={classes.inventoryValue}>
                  {convertMetricVolume(volume, measurementCode, 1)}
                  {` ${measurementCode}`}
                </span>
              </Grid>
              <Grid item container direction="row" justifyContent="space-between">
                <span className={classes.inventoryProperty}>{t("weight")}</span>
                <span className={classes.inventoryValue}>{weight}</span>
              </Grid>
            </Grid>
          </div>
        </Grid>
      </SkeletonComponent>
    );
  };

  const renderInventorySection = () => {
    return (
      <>
        {Object.keys(addressDestinationItems).map((addressId) => {
          const address = destinationAddresses.find(
            (address) => String(address.id) === String(addressId?.replace("id-", ""))
          );
          const items = addressDestinationItems[addressId];

          if (!address || !items) return null;

          return renderInventoryBox(t("residence"), items, address.longAddress || "", "#308CDB");
        })}

        {Object.keys(customDestinationItems).map((destinationId) => {
          const destination = destinationList.find(
            (destination) => String(destination.id) === String(destinationId?.replace("id-", ""))
          );
          const items = customDestinationItems[destinationId];

          if (!destination || !items) return null;

          return renderInventoryBox(destination.name, items, destination.name, destination.color);
        })}

        {Object.keys(customModeDestinationItems).map((modeDestinationId) => {
          const modeDestination = modeDestinationList?.find(
            (modeDestination) =>
              String(modeDestination.id) === String(modeDestinationId?.replace("id-", ""))
          );
          const items = customModeDestinationItems[modeDestinationId];

          if (!modeDestination || !items) return null;

          return renderInventoryBox(modeDestination.name, items, modeDestination.name);
        })}
      </>
    );
  };

  return (
    <Grid container direction="column" data-cy="estimate-inventory">
      <Grid item className={classes.subTitle}>
        <span data-cy="estimate-inventory-title">{t("inventory")}</span>
      </Grid>

      {Object.keys(addressDestinationItems).length <= 0 &&
        Object.keys(customDestinationItems).length <= 0 &&
        !loading && (
          <Grid item className={classes.empty}>
            <span data-cy="estimate-inventory-empty">{t("youHavenTAddedAnyItemsYet")}</span>
          </Grid>
        )}

      <div className={classes.inventorySection} data-cy="estimate-inventory-section">
        {renderInventorySection()}
      </div>
    </Grid>
  );
};

export default React.memo(EstimateInventorySection);
